import React, { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, Libraries, useJsApiLoader } from "@react-google-maps/api";
import { MapPin } from "lucide-react";
import { isInApp } from "src/helpers/parentMessageHandler";

const defaultCenter = {
  lat: 37.4275,
  lng: -122.1697,
};

const mapStyles = [
  {
    featureType: "all",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#E3F2FD" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#F5F5F5" }],
  },
];

const GOOGLE_MAP_LIBRARIES: Libraries = ["places"];

interface LatLng {
  lat: number;
  lng: number;
}

interface MapViewProps {
  onLocationSelect: (location: LatLng) => void;
  initialLocation: LatLng;
  region: LatLng;
  onRegionChange: (location: LatLng) => void;
  provider?: string;
  style?: React.CSSProperties;
  showsUserLocation?: boolean;
  followsUserLocation?: boolean;
}

const MapView: React.FC<MapViewProps> = ({
  onLocationSelect,
  initialLocation,
  region,
  onRegionChange,
  provider = "google",
  style = {},
  showsUserLocation = true,
  followsUserLocation = true,
}) => {
  const [currentLocation, setCurrentLocation] = useState(initialLocation);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const lastLocationRef = useRef(currentLocation);
  const hasSetInitialLocation = useRef(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  useEffect(() => {
    if (
      !hasSetInitialLocation.current &&
      showsUserLocation &&
      navigator.geolocation
    ) {
      setIsLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentLocation(userLocation);
          lastLocationRef.current = userLocation;
          onLocationSelect(userLocation);
          onRegionChange(userLocation);
          hasSetInitialLocation.current = true;
          setIsLocationLoading(false);
        },
        (error) => {
          console.error("Geolocation error:", error);
          // In React Native WebView, geolocation might fail
          // Use the initial location as fallback
          setCurrentLocation(initialLocation);
          lastLocationRef.current = initialLocation;
          onLocationSelect(initialLocation);
          onRegionChange(initialLocation);
          hasSetInitialLocation.current = true;
          setIsLocationLoading(false);
        },
        {
          timeout: 10000,
          enableHighAccuracy: true,
          maximumAge: 60000,
        }
      );
    } else if (!hasSetInitialLocation.current) {
      // If geolocation is not available or not requested, use initial location
      setCurrentLocation(initialLocation);
      lastLocationRef.current = initialLocation;
      onLocationSelect(initialLocation);
      onRegionChange(initialLocation);
      hasSetInitialLocation.current = true;
    }
  }, [initialLocation, onLocationSelect, onRegionChange, showsUserLocation]);

  useEffect(() => {
    if (map && region) {
      const newCenter = new google.maps.LatLng(region.lat, region.lng);
      map.panTo(newCenter);
    }
  }, [region, map]);

  const updateLocation = useCallback(() => {
    if (!map) return;
    const center = map.getCenter();
    if (!center) return;
    const newLocation = {
      lat: center.lat(),
      lng: center.lng(),
    };

    const hasChanged =
      Math.abs(newLocation.lat - lastLocationRef.current.lat) > 0.00001 ||
      Math.abs(newLocation.lng - lastLocationRef.current.lng) > 0.00001;

    if (hasChanged) {
      lastLocationRef.current = newLocation;
      setCurrentLocation(newLocation);
      onLocationSelect(newLocation);
      onRegionChange(newLocation);
    }
  }, [map, onLocationSelect, onRegionChange]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error loading map</div>
          <div className="text-sm text-gray-600">
            {isInApp()
              ? "Please check your internet connection"
              : "Unable to load Google Maps"}
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--app-primary)] mx-auto mb-2"></div>
          <div className="text-sm text-gray-600">Loading map...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%", ...style }}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={currentLocation}
        zoom={15}
        onLoad={(mapInstance) => {
          setMap(mapInstance);
          mapRef.current = mapInstance;
        }}
        onDragEnd={updateLocation}
        onZoomChanged={updateLocation}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          styles: mapStyles,
        }}
      />

      {/* Center Pin */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full w-6 h-6 pointer-events-none z-10">
        <MapPin className="w-10 h-10 text-[var(--app-primary)]" />
      </div>

      {/* Location Loading Indicator */}
      {isLocationLoading && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white px-3 py-2 rounded-lg shadow-md z-20">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--app-primary)] mr-2"></div>
            <span className="text-sm text-gray-700">
              Getting your location...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;
